import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Locale } from "@dto/Locale";
import { User } from "@dto/User";
import { RootState } from "./store";
import {
  setCookie,
  CookieKeys,
  getCookie,
  removeAllCookies,
} from "@lib/cookies";
import { logout, setAccessToken } from "./visitWidgetApi";
import { setOtpToken } from "./visitWidgetApi";
/**
 * The current step of the auth process.
 */
export type AuthView =
  | "signInEmail"
  | "signInPassword"
  | "signInSms"
  | "register"
  | "smsVerify"
  | "resetPasswordRequest"
  | "resetPasswordRequestSent"
  | "resetPassword"
  | "resetPasswordExpired"
  | "magicLinkSent"
  | "magicLinkExpired"
  | "adminOtpVerify";

/**
 * The current workflow of the auth process.
 */
export type AuthFlow = "signIn" | "register";

interface SessionReducerState {
  email: null | string;
  isEmailRefreshed: boolean;
  authToken: null | string;
  otpToken: null | string;
  smsAuthToken: null | {
    phone: string;
    temporarySmsToken: string;
  };
  resetPasswordToken: null | string;
  user: User | null;
  settings: {
    locale: Locale;
  };
  authView: AuthView;
  authFlow: AuthFlow;
  isAuthViewOpen: boolean;
  hasPromptedAuthOnAdd: boolean;
}

const initialState: SessionReducerState = {
  email: null,
  isEmailRefreshed: false,
  authToken: null,
  otpToken: null,
  smsAuthToken: null,
  resetPasswordToken: null,
  user: null,
  settings: {
    locale: "en",
  },
  authView: "signInEmail",
  authFlow: "signIn",
  isAuthViewOpen: false,
  hasPromptedAuthOnAdd: false,
};

export const refreshAuthEmailAction = createAsyncThunk<string | null>(
  "session/refreshAuthEmail",
  async (_, { getState }) => {
    const { email } = (getState() as RootState).session;
    if (email) {
      return email;
    }
    return (await getCookie<string | null>(CookieKeys.authEmail, null)) ?? null;
  },
);

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    setEmailAction: (
      state: SessionReducerState,
      action: PayloadAction<{ email: string }>,
    ) => {
      state.email = action.payload.email;
      setCookie(CookieKeys.authEmail, action.payload.email, 1);
    },
    setOtpTokenAction: (
      state: SessionReducerState,
      action: PayloadAction<{ otpToken: string | null }>,
    ) => {
      state.otpToken = action.payload.otpToken;
    },
    setSettingsAction: (
      state: SessionReducerState,
      action: PayloadAction<{ locale: Locale }>,
    ) => {
      state.settings.locale = action.payload.locale;
    },
    setUserAction: (
      state: SessionReducerState,
      action: PayloadAction<{ user: User; shouldResetGuestUser?: boolean }>,
    ) => {
      state.user = action.payload.user;
    },
    signInAction: (
      state: SessionReducerState,
      action: PayloadAction<{ authToken: string }>,
    ) => {
      state.authToken = action.payload.authToken;
    },
    setSMSTokenAction: (
      state: SessionReducerState,
      action: PayloadAction<{
        smsAuthToken: { phone: string; temporarySmsToken: string };
      }>,
    ) => {
      state.smsAuthToken = action.payload.smsAuthToken;
    },
    signOutAction: (state: SessionReducerState) => {
      state.authView = "signInEmail";
      state.authFlow = "signIn";
      state.email = null;
      state.authToken = null;
      state.otpToken = null;
      state.smsAuthToken = null;
      state.resetPasswordToken = null;
      state.user = null;
      state.isAuthViewOpen = false;

      void removeAllCookies();
      void logout();
      setAccessToken(undefined);
      setOtpToken(undefined);
    },
    setAuthViewAction: (
      state: SessionReducerState,
      action: PayloadAction<{
        authView: AuthView;
      }>,
    ) => {
      state.authView = action.payload.authView;
    },
    setResetPasswordTokenAction: (
      state: SessionReducerState,
      action: PayloadAction<{ resetPasswordToken: string }>,
    ) => {
      state.resetPasswordToken = action.payload.resetPasswordToken;
    },
    resetAuthViewAction: (state: SessionReducerState) => {
      state.authView = "signInEmail";
      state.authFlow = "signIn";
      state.email = null;
      state.authToken = null;
      state.otpToken = null;
      state.smsAuthToken = null;
      state.resetPasswordToken = null;
      state.user = null;
    },
    setAuthFlowAction: (
      state: SessionReducerState,
      action: PayloadAction<{ authFlow: AuthFlow }>,
    ) => {
      state.authFlow = action.payload.authFlow;
    },
    setIsAuthViewOpenAction: (
      state: SessionReducerState,
      action: PayloadAction<{ isAuthViewOpen: boolean }>,
    ) => {
      state.isAuthViewOpen = action.payload.isAuthViewOpen;
      3;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(refreshAuthEmailAction.fulfilled, (state, action) => {
      state.email = action.payload;
      state.isEmailRefreshed = true;
    });
    builder.addCase(refreshAuthEmailAction.pending, (state, _action) => {
      state.isEmailRefreshed = false;
    });
    builder.addCase(refreshAuthEmailAction.rejected, (state, _action) => {
      state.email = null;
      state.isEmailRefreshed = true;
    });
  },
});

export const selectEmail = (state: RootState) => {
  return state.session.email;
};

export const selectIsEmailRefreshed = (state: RootState) => {
  return state.session.isEmailRefreshed;
};

export const selectUserRoles = (state: RootState) => {
  return state.session.user?.roles ?? [];
};

export const selectIsUserInRole = (role: string) => (state: RootState) => {
  return selectUserRoles(state).findIndex((x) => x === role) > -1;
};

export const selectIsUserInAdminRole = (state: RootState) => {
  return selectIsUserInRole("admin")(state);
};

export const selectOtpToken = (state: RootState) => {
  return state.session.otpToken;
};

export const selectUser = (state: RootState) => {
  return state.session.user;
};

export const selectUserId = (state: RootState): number | undefined => {
  return state.session.user?.id;
};

export const selectLocale = (state: RootState): Locale => {
  return state.session.settings.locale;
};

export const selectAuthView = (state: RootState): AuthView => {
  return state.session.authView;
};

export const selectSmsAuth = (state: RootState) => {
  return state.session.smsAuthToken;
};

export const selectAuthToken = (state: RootState) => {
  return state.session.authToken;
};

export const selectResetPasswordToken = (state: RootState) => {
  return state.session.resetPasswordToken;
};

export const selectAuthFlow = (state: RootState) => {
  return state.session.authFlow;
};

export const selectIsAuthViewOpen = (state: RootState) => {
  return state.session.isAuthViewOpen;
};

export const selectIsUserAuthenticated = (state: RootState) => {
  return (
    typeof state.session.authToken === "string" &&
    state.session.authToken.length > 0
  );
};

export const {
  setEmailAction,
  setOtpTokenAction,
  setSettingsAction,
  setSMSTokenAction,
  setUserAction,
  signInAction,
  signOutAction,
  setAuthViewAction,
  setResetPasswordTokenAction,
  resetAuthViewAction,
  setAuthFlowAction,
  setIsAuthViewOpenAction,
} = sessionSlice.actions;

// eslint-disable-next-line import/no-default-export
export default sessionSlice.reducer;
export type { SessionReducerState };
